<template>
	<v-card class="flat-border-card form" flat>
		<v-card-title>
			<h3 class="headline mb-0">Address</h3>
			<v-spacer></v-spacer>
			<v-btn v-if="value.length" small flat @click="showSummary = !showSummary">
				<v-icon small class="mr-1">{{ showSummary ? 'mdi-history' : 'mdi-card-text-outline' }}</v-icon>
				{{ showSummary ? 'Show History' : 'Show Summary' }}
			</v-btn>
			<v-tooltip v-if="canEdit" bottom>
				<template v-slot:activator="{ on }">
					<v-btn icon aria-label="Add residential address" v-on="on" @click="addAddress">
						<v-icon>add</v-icon>
					</v-btn>
				</template>
				<span>Add Address</span>
			</v-tooltip>
			<v-tooltip v-if="showSummary && canEdit && currentAddress" bottom>
				<template v-slot:activator="{ on }">
					<v-btn icon aria-label="Edit current residential address" v-on="on" @click="editAddress(currentAddress)">
						<v-icon>edit</v-icon>
					</v-btn>
				</template>
				<span>Edit Address</span>
			</v-tooltip>
		</v-card-title>

		<v-card-text v-if="showSummary" class="text-xs-left">
			<residential-address-form v-if="currentAddress" :value="currentAddress"></residential-address-form>
			<v-alert v-else :value="true" color="info" icon="info" outline>
				<span v-if="nextScheduledAddress">
					No residential address is currently effective. The next address is scheduled for
					{{ nextScheduledAddress.effectiveDate | formatDate }}.
				</span>
				<span v-else>No residential address has been recorded.</span>
			</v-alert>
		</v-card-text>

		<v-card-text v-else>
			<v-data-table
				v-if="sortedAddresses.length"
				:headers="headers"
				:items="sortedAddresses"
				hide-actions
				disable-initial-sort
				class="address-table elevation-1"
			>
				<template slot="items" slot-scope="props">
					<tr>
						<td class="text-xs-left text-no-wrap">{{ props.item.effectiveDate | formatDate }}</td>
						<td class="text-xs-left">{{ formatAddressInline(props.item) }}</td>
						<td class="text-xs-left">
							<v-chip small label :color="getStatusColor(props.item)" :dark="getStatus(props.item) !== 'Previous'">
								{{ getStatus(props.item) }}
							</v-chip>
						</td>
						<td v-if="canEdit" class="text-xs-center px-0">
							<v-menu offset-y>
								<template v-slot:activator="{ on }">
									<v-btn small icon flat aria-label="Address actions" v-on="on">
										<v-icon>mdi-dots-horizontal</v-icon>
									</v-btn>
								</template>
								<v-list>
									<v-list-tile @click="editAddress(props.item)">
										<v-list-tile-action><v-icon>edit</v-icon></v-list-tile-action>
										<v-list-tile-title>Edit</v-list-tile-title>
									</v-list-tile>
									<v-list-tile @click="confirmDelete(props.item)">
										<v-list-tile-action><v-icon>delete</v-icon></v-list-tile-action>
										<v-list-tile-title>Delete</v-list-tile-title>
									</v-list-tile>
								</v-list>
							</v-menu>
						</td>
					</tr>
				</template>
			</v-data-table>

			<div v-else class="empty-address-state text-xs-center py-4">
				<v-icon color="grey lighten-1" size="42">mdi-map-marker-outline</v-icon>
				<div class="subheading mt-2">No residential addresses recorded</div>
				<div class="caption grey--text text--darken-1 mt-1">
					{{ canEdit ? 'Add an address to begin the employee\'s history.' : 'There is no address history to display.' }}
				</div>
			</div>
		</v-card-text>

		<residential-address-editor
			v-if="showEditor"
			:dialog="showEditor"
			:address="editingAddress"
			:excluded-effective-dates="excludedEffectiveDates"
			:is-saving="isSaving"
			@save="saveAddress"
			@cancel="closeEditor"
		></residential-address-editor>

		<confirmation-dialog
			v-if="addressToDelete"
			:dialog="!!addressToDelete"
			title="Delete Residential Address"
			confirm-button-text="Delete"
			:is-confirming="isDeleting"
			@onConfirm="deleteAddress"
			@onCancel="addressToDelete = null"
		>
			<div class="pt-3 pb-2 text-xs-left">
				Are you sure you want to permanently delete the address effective
				<strong>{{ addressToDelete.effectiveDate | formatDate }}</strong>?
			</div>
		</confirmation-dialog>
	</v-card>
</template>

<script>
	import moment from 'moment';
	import toast from '@/services/toasts/index.js';
	import ResidentialAddressEditor from '@/components/employee/ResidentialAddressEditor';
	import ResidentialAddressForm from '@/components/employee/forms/ResidentialAddressForm';

	export default {
		components: { ResidentialAddressEditor, ResidentialAddressForm },
		props: {
			value: {
				type: Array,
				default: () => []
			},
			employeeId: {
				type: String,
				required: true
			},
			canEdit: {
				type: Boolean,
				default: false
			}
		},
		data() {
			return {
				showSummary: true,
				showEditor: false,
				editingAddress: null,
				addressToDelete: null,
				isSaving: false,
				isDeleting: false
			};
		},
		computed: {
			headers() {
				const headers = [
					{ text: 'Effective Date', value: 'effectiveDate' },
					{ text: 'Address', value: 'addressLine1', sortable: false },
					{ text: 'Status', value: 'status', sortable: false }
				];

				if (this.canEdit) {
					headers.push({ text: '', value: 'actions', sortable: false, width: '60px' });
				}

				return headers;
			},
			today() {
				return moment().format('YYYY-MM-DD');
			},
			sortedAddresses() {
				return [...this.value].sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate));
			},
			currentAddress() {
				return this.sortedAddresses.find(address => address.effectiveDate <= this.today) || null;
			},
			nextScheduledAddress() {
				return [...this.value]
					.filter(address => address.effectiveDate > this.today)
					.sort((a, b) => a.effectiveDate.localeCompare(b.effectiveDate))[0] || null;
			},
			excludedEffectiveDates() {
				return this.value
					.filter(address => !this.editingAddress || address.id !== this.editingAddress.id)
					.map(address => address.effectiveDate.substring(0, 10));
			}
		},
		methods: {
			addAddress() {
				this.editingAddress = null;
				this.showEditor = true;
			},
			editAddress(address) {
				this.editingAddress = address;
				this.showEditor = true;
			},
			closeEditor() {
				this.showEditor = false;
				this.editingAddress = null;
			},
			confirmDelete(address) {
				this.addressToDelete = address;
			},
			async saveAddress(address) {
				this.isSaving = true;
				const isEditing = !!address.id;
				const action = isEditing ? 'employees/updateResidentialAddress' : 'employees/createResidentialAddress';
				const result = await this.$store.dispatch(action, {
					employeeId: this.employeeId,
					address
				});
				this.isSaving = false;

				if (!result.isSuccess) return;

				const addresses = isEditing
					? this.value.map(item => (item.id === result.data.id ? result.data : item))
					: [...this.value, result.data];

				this.$emit('input', addresses);
				this.closeEditor();
				toast.success(isEditing ? 'Residential address updated' : 'Residential address added');
			},
			async deleteAddress() {
				this.isDeleting = true;
				const result = await this.$store.dispatch('employees/deleteResidentialAddress', {
					employeeId: this.employeeId,
					addressId: this.addressToDelete.id
				});
				this.isDeleting = false;

				if (!result.isSuccess) return;

				this.$emit('input', this.value.filter(address => address.id !== this.addressToDelete.id));
				this.addressToDelete = null;
				toast.success('Residential address deleted');
			},
			getStatus(address) {
				if (address.effectiveDate > this.today) return 'Scheduled';
				if (this.currentAddress && address.id === this.currentAddress.id) return 'Current';
				return 'Previous';
			},
			getStatusColor(address) {
				const status = this.getStatus(address);
				if (status === 'Current') return 'success';
				if (status === 'Scheduled') return 'info';
				return 'grey lighten-3';
			},
			formatAddressInline(address) {
				return [
					address.addressLine1,
					address.addressLine2,
					address.suburb,
					address.state,
					address.postcode,
					address.country || 'Australia'
				]
					.filter(Boolean)
					.join(', ');
			}
		}
	};
</script>

<style scoped>
	.empty-address-state {
		border: 1px dashed #d3d8dc;
		border-radius: 4px;
	}

	@media (max-width: 600px) {
		.address-table {
			overflow-x: auto;
		}
	}
</style>
